import { useCallback, useState, useMemo } from 'react'
import { Input, Button, Card } from '@heroui/react'
import { checkAdmission } from '@/services/tuition'
import { TUITION_COPY, TUITION_PROGRAMS as BASE_TUITION_PROGRAMS } from '@/constants/tuition'
import { getWebEnv } from '@/env'
import Header from '@/components/public/Header'
import { email as emailSchema, FIELD_LIMITS } from '@leangap/types'

export default function TuitionPage() {
	const [email, setEmail] = useState('')
	const [isSubmitting, setIsSubmitting] = useState(false)
	const [status, setStatus] = useState<
		'idle' | 'admitted' | 'not-found' | 'not-admitted-yet' | 'error'
	>('idle')
	const [error, setError] = useState('')
	const [userData, setUserData] = useState<{ firstName?: string; lastName?: string } | null>(null)
	const [touched, setTouched] = useState(false)

	// Validate email with TLD check (same schema as server)
	const validation = useMemo(() => {
		const trimmed = email.trim()
		if (!trimmed) return { valid: false, error: null } // empty = no error shown, but can't submit
		const result = emailSchema.safeParse(trimmed)
		if (result.success) return { valid: true, error: null }
		return {
			valid: false,
			error: result.error.issues[0]?.message || 'Please enter a valid email address',
		}
	}, [email])

	const displayError = touched && validation.error ? validation.error : null

	const handleSubmit = useCallback(
		async (e: React.FormEvent) => {
			e.preventDefault()
			setError('')
			setTouched(true)
			if (!validation.valid) {
				return
			}
			// Schema normalizes email (trim + lowercase) in result.data
			const result = emailSchema.safeParse(email)
			const normalized = result.success ? result.data : email.trim().toLowerCase()
			setIsSubmitting(true)
			setUserData(null)
			try {
				const data = await checkAdmission(normalized)
				if ((data as any).notFound) {
					setStatus('not-found')
				} else if ((data as any).isAdmitted) {
					setStatus('admitted')
					setUserData({ firstName: (data as any).firstName, lastName: (data as any).lastName })
				} else {
					setStatus('not-admitted-yet')
				}
			} catch (err: any) {
				const msg = err?.message ?? TUITION_COPY.error
				setError(msg)
				setStatus('error')
			} finally {
				setIsSubmitting(false)
			}
		},
		[email, validation.valid]
	)

	const getProgramUrl = (baseUrl: string) => {
		if (!userData) return baseUrl
		const params = new URLSearchParams({
			email: email.trim(),
			firstname: userData.firstName ?? '',
			lastname: userData.lastName ?? '',
		})
		return `${baseUrl}?${params.toString()}`
	}

	const webEnv = getWebEnv()
	const thirdLabel = webEnv.VITE_TUITION_THIRD_LABEL?.trim()
	const thirdUrl = webEnv.VITE_TUITION_THIRD_URL?.trim()

	const perOptionSoldOut = new Set<string>()
	if (webEnv.VITE_TUITION_ONLINE_SOLD_OUT === 'true') perOptionSoldOut.add('online')
	if (webEnv.VITE_TUITION_SF_SOLD_OUT === 'true') perOptionSoldOut.add('sf')
	if (webEnv.VITE_TUITION_NEXT_YEAR_SOLD_OUT === 'true') perOptionSoldOut.add('next-year')

	const TUITION_PROGRAMS = BASE_TUITION_PROGRAMS.map((p, idx) => {
		if (idx === BASE_TUITION_PROGRAMS.length - 1) {
			return {
				...p,
				label: thirdLabel || p.label,
				url: thirdUrl || p.url,
			}
		}
		return p
	})

	const showThird = webEnv.VITE_TUITION_SHOW_THIRD === 'true'
	const programsToRender = showThird
		? TUITION_PROGRAMS
		: TUITION_PROGRAMS.slice(0, BASE_TUITION_PROGRAMS.length - 1)

	const isProgramSoldOut = (id: string) => perOptionSoldOut.has(id)

	return (
		<div className="m-auto w-full max-w-md">
			<div className="flex flex-col items-center gap-4">
				<Header />
				{status !== 'admitted' && (
					<>
						<h1 className="text-center text-2xl font-bold">{TUITION_COPY.welcome}</h1>
						<p className="text-default-600 text-center text-lg">{TUITION_COPY.description}</p>
					</>
				)}
			</div>
			<Card className="mt-8">
				<div className="p-6">
					{status !== 'admitted' && (
						<form onSubmit={handleSubmit} className="space-y-4">
							<Input
								label="Student Email"
								type="email"
								maxLength={FIELD_LIMITS.email}
								value={email}
								onValueChange={(v: string) => {
									setEmail(v)
									setTouched(true)
								}}
								placeholder="Email used in your application"
								isRequired
								isInvalid={!!displayError}
								errorMessage={displayError || undefined}
							/>
							<Button
								type="submit"
								color="primary"
								isDisabled={!validation.valid || isSubmitting}
								isLoading={isSubmitting}
								className="w-full"
							>
								Access Payment
							</Button>
						</form>
					)}

					{status === 'admitted' && (
						<div className="space-y-4">
							<div className="text-success text-center font-medium">{TUITION_COPY.admitted}</div>
							<hr />
							<div className="space-y-3">
								<h3 className="text-center font-medium">Available Programs</h3>
								<div className="flex flex-col gap-3">
									{programsToRender.map((p) => {
										const isSoldOut = isProgramSoldOut(p.id)
										return (
											<Button
												key={p.id}
												as="a"
												href={getProgramUrl(p.url)}
												target="_blank"
												rel="noopener noreferrer"
												className={`bg-purple-800 text-white ${isSoldOut ? 'pointer-events-none opacity-50' : ''}`}
												disabled={isSoldOut}
												aria-disabled={isSoldOut}
											>
												{p.label}
												{isSoldOut ? (
													<span className="bg-danger ml-2 rounded-full px-2 py-1 text-xs text-white">
														SOLD OUT
													</span>
												) : null}
											</Button>
										)
									})}
								</div>
							</div>
						</div>
					)}

					{status === 'not-found' && (
						<div className="text-warning mt-6 text-center">{TUITION_COPY.notFound}</div>
					)}

					{status === 'not-admitted-yet' && (
						<div className="text-warning mt-6 text-center">{TUITION_COPY.notAdmittedYet}</div>
					)}

					{status === 'error' && (
						<div className="text-danger mt-6 text-center">{error || TUITION_COPY.error}</div>
					)}
				</div>
			</Card>
		</div>
	)
}
